import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { backendClient } from "@/lib/backendClient";
import { getAllConfiguredCombos, useSelectionStore } from "@/store/selection";
import { useSessionStore } from "@/store/session";
import type {
    ProductPrice,
    ProductSurface,
    TotalPriceResult,
} from "../price.types";
import { parsePriceResponse } from "../mappers/price.parse";
import { mapCombosToProductInputs } from "@/models/order";
import GET_TOTAL_PRICE_SUN_PROTECTION from "../graphql/GetTotalPriceSunProtection.graphql";

export function useTotalPrice(): TotalPriceResult {
    const state = useSelectionStore();
    const { backendDealId, discountCode } = useSessionStore();

    const productInputs = useMemo(() => {
        const combos = getAllConfiguredCombos(state).map((combo) => ({
            ...combo,
            selectedProductIds: combo.selectedProductIds.filter(
                (id) => !!combo.windowSelections[id],
            ),
        }));
        return mapCombosToProductInputs(combos);
    }, [state]);

    const configuredProductIds = useMemo(
        () => productInputs.map((p) => p.productId),
        [productInputs],
    );

    const skip = !backendDealId || productInputs.length === 0;

    const { data, loading, error } = useQuery<unknown>(
        GET_TOTAL_PRICE_SUN_PROTECTION,
        {
            client: backendClient,
            variables: {
                dealId: backendDealId,
                productInputs,
                discountCode: discountCode || null,
            },
            skip,
        },
    );

    const parsed = useMemo(() => parsePriceResponse(data), [data]);

    const productPrices: ProductPrice[] = useMemo(() => {
        if (!parsed) return [];
        return parsed.products
            .filter((p) => configuredProductIds.includes(p.product.id))
            .map((p) => ({
                productId: p.product.id,
                sunProtectionProductId: p.sunProtectionProductId,
                colorId: p.colorId,
                total: p.total,
            }));
    }, [parsed, configuredProductIds]);

    const totals = parsed?.productsTotals ?? null;

    const productSurfaces: ProductSurface[] = useMemo(
        () => parsed?.productSurfaces ?? [],
        [parsed],
    );

    return { productPrices, totals, productSurfaces, loading, error };
}
